import RejectReasonModal from '@/ui/components/process-details/process-island/products-tab/reject-reason-modal/reject-reason-modal'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CheckIcon,
  CrossIcon2,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  WarningCircleIcon
} from '@itsrever/design-system'
import { clsx } from 'clsx'
import { NewReview, ReviewStatus } from '@/domain/review'

interface ReviewButtonsProps {
  index: number
  reviews: NewReview[]
  setReviews: (reviews: NewReview[]) => void
}

const ReviewButtons: React.FC<ReviewButtonsProps> = ({
  index,
  reviews,
  setReviews
}) => {
  const { t } = useTranslation()
  const status = reviews[index]?.status
  const [rejectModalOpen, setRejectModalOpen] = useState(-1)
  const handleChangeReview = (status: ReviewStatus, index: number) => {
    if (status === 'DECLINED') {
      updateReviews(reviews, setReviews, index, 'PENDING')
      setRejectModalOpen(index)
    } else {
      updateReviews(reviews, setReviews, index, status)
    }
  }
  function updateReviews(
    reviews: NewReview[],
    setReviews: (reviews: NewReview[]) => void,
    index: number,
    status: ReviewStatus,
    rejectReason?: string,
    showRejectReason?: boolean
  ) {
    const newReviews = [...reviews]
    if (rejectReason) {
      newReviews[index] = {
        ...newReviews[index],
        status,
        rejectReason: rejectReason,
        showRejectReason: showRejectReason
      }
    } else {
      if (newReviews[index] && newReviews[index].rejectReason)
        delete newReviews[index].rejectReason
      newReviews[index] = {
        ...newReviews[index],
        status
      }
    }
    setReviews(newReviews)
  }

  const showReasonTooltip =
    reviews[index]?.rejectReason?.length &&
    (reviews[index]?.rejectReason?.length ?? 0) > 35

  return (
    <>
      <div className="flex flex-col items-end gap-1">
        <div className="flex w-fit gap-1 rounded-lg bg-slate-200 p-1">
          <button
            onClick={() => handleChangeReview('APPROVED', index)}
            className={clsx(
              'group flex min-w-8 cursor-pointer items-center justify-center rounded-md bg-slate-200 p-1 pr-2',
              status === 'APPROVED'
                ? 'bg-white shadow-sm'
                : 'hover:bg-slate-300'
            )}
          >
            <CheckIcon className="size-5 text-green-500" />
            <p className={clsx(' text-sm font-light')}>
              {t('products_tab.approve')}
            </p>
          </button>

          <button
            onClick={() => handleChangeReview('DECLINED', index)}
            className={clsx(
              'group flex min-w-8 cursor-pointer items-center justify-center gap-1 rounded-md bg-slate-200 p-1',
              status === 'DECLINED' || rejectModalOpen === index
                ? 'bg-white shadow-sm'
                : 'hover:bg-slate-300'
            )}
          >
            <CrossIcon2 className="ml-2 size-3 text-red-500" />
            <div
              className={clsx(
                'grid transition-all duration-300 ease-in-out',
                status === 'DECLINED' || rejectModalOpen === index
                  ? 'grid-cols-[1fr]'
                  : 'grid-cols-[1fr] group-hover:grid-cols-[1fr]'
              )}
            >
              <p
                className={clsx(
                  'overflow-hidden text-sm font-light group-hover:visible group-hover:pr-2',
                  status === 'DECLINED' || rejectModalOpen === index
                    ? 'visible pr-2'
                    : 'visible pr-2'
                )}
              >
                {t('products_tab.decline')}
              </p>
            </div>
          </button>

          <button
            onClick={() => handleChangeReview('MISSING', index)}
            className={clsx(
              'group flex min-w-8 cursor-pointer items-center justify-center gap-1 rounded-md bg-slate-200 p-1 px-2',
              status === 'MISSING' ? 'bg-white shadow-sm' : 'hover:bg-slate-300'
            )}
          >
            <WarningCircleIcon className={clsx('size-4 text-orange-500')} />
            <p className={clsx(' text-sm font-light')}>
              {t('products_tab.missing')}
            </p>
          </button>
        </div>
        {status === 'DECLINED' ? (
          <Tooltip placement="bottom">
            <TooltipTrigger>
              <p className="max-w-64 truncate text-xs font-light">
                <span className="text-xs text-slate-800">
                  {t('titles_li.reason')}:{' '}
                </span>
                <span className="text-xs text-slate-500">
                  {reviews[index]?.rejectReason}
                </span>
              </p>
            </TooltipTrigger>
            {showReasonTooltip ? (
              <TooltipContent>
                <p className="max-w-64 text-xs font-light">
                  {reviews[index]?.rejectReason}
                </p>
              </TooltipContent>
            ) : null}
          </Tooltip>
        ) : null}
      </div>

      <RejectReasonModal
        index={index}
        isOpen={rejectModalOpen === index}
        setIsOpen={setRejectModalOpen}
        reviews={reviews}
        setReviews={setReviews}
        updateReviews={updateReviews}
      />
    </>
  )
}

export default ReviewButtons
